// side_deco_controller.js
// Schwebende SVG-Icons in den Seitenrändern (außerhalb 1200px Container).
// Partikel haben absolute Seiten-Koordinaten und scrollen mit dem Inhalt mit.
// SVG-URLs werden via data-side-deco-svgs-value (JSON-Array) übergeben.

import { Controller } from "@hotwired/stimulus"

const CONTAINER_WIDTH = 1200
const COUNT_PER_SIDE  = 18
const MAX_OPACITY     = 0.18
const MIN_SIZE        = 18
const MAX_SIZE        = 72
const SIZE_SPEED_MIN  = 0.02
const SIZE_SPEED_MAX  = 0.10
const DRIFT_MIN       = 0.06
const DRIFT_MAX       = 0.22
const SWAY_AMP        = 18
const SWAY_SPEED      = 0.004
const FADE_IN_SPEED   = 0.006
const FADE_OUT_SPEED  = 0.005

export default class extends Controller {
  static values = { svgs: { type: Array, default: [] } }

  connect() {
    this.canvas = document.createElement("canvas")
    Object.assign(this.canvas.style, {
      position: "absolute", inset: "0",
      width: "100%", height: "100%", display: "block"
    })
    this.element.appendChild(this.canvas)
    this.ctx         = this.canvas.getContext("2d")
    this.particles   = []
    this.loadedImages = []
    this.raf         = null

    this.resize()
    this.addListeners()

    const urls = this.svgsValue
    if (urls.length) {
      this.loadImages(urls).then(() => {
        this.spawnAll()
        this.tick()
      })
    } else {
      this.spawnAll()
      this.tick()
    }
  }

  disconnect() {
    cancelAnimationFrame(this.raf)
    window.removeEventListener("resize", this._onResize)
    this._ro?.disconnect()
  }

  // ── Bilder vorladen ───────────────────────────────────────────────────────

  async loadImages(urls) {
    const results = await Promise.allSettled(
      urls.map(url => new Promise((resolve, reject) => {
        const img = new Image()
        img.onload  = () => resolve(img)
        img.onerror = () => reject(url)
        img.src = url
      }))
    )
    this.loadedImages = results
      .filter(r => r.status === "fulfilled")
      .map(r => r.value)
  }

  // ── Layout ────────────────────────────────────────────────────────────────

  resize() {
    this.VW    = window.innerWidth
    this.VH    = window.innerHeight
    this.pageH = Math.max(document.documentElement.scrollHeight, this.VH)
    this.canvas.width  = this.VW
    this.canvas.height = this.VH

    const gap        = (this.VW - CONTAINER_WIDTH) / 2
    this.leftEnd     = Math.max(0, gap - 20)
    this.rightStart  = Math.min(this.VW, this.VW - gap + 20)
    this.hasSpace    = gap > 50

    if (this.particles.length) this.spawnAll()
  }

  // ── Partikel ──────────────────────────────────────────────────────────────

  spawnAll() {
    this.particles = [
      ...Array.from({ length: COUNT_PER_SIDE }, (_, i) =>
        this.make("left",  (i / COUNT_PER_SIDE) * this.pageH)),
      ...Array.from({ length: COUNT_PER_SIDE }, (_, i) =>
        this.make("right", (i / COUNT_PER_SIDE) * this.pageH)),
    ]
  }

  make(side, startY) {
    const baseX = side === "left"
      ? 20 + Math.random() * Math.max(0, this.leftEnd - 40)
      : this.rightStart + 20 + Math.random() * Math.max(0, (this.VW - this.rightStart) - 40)

    const imgs = this.loadedImages
    const img  = imgs.length
      ? imgs[Math.floor(Math.random() * imgs.length)]
      : null

    return {
      baseX,
      x:        baseX,
      y:        startY ?? Math.random() * this.pageH,
      size:     MIN_SIZE + Math.random() * (MAX_SIZE - MIN_SIZE),
      sizeSpeed:(SIZE_SPEED_MIN + Math.random() * (SIZE_SPEED_MAX - SIZE_SPEED_MIN))
                  * (Math.random() < 0.5 ? 1 : -1),
      img,
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() < 0.5 ? 1 : -1) * (0.003 + Math.random() * 0.007),
      driftY:   DRIFT_MIN + Math.random() * (DRIFT_MAX - DRIFT_MIN),
      phase:    Math.random() * Math.PI * 2,
      opacity:  0,
      maxOpacity: MAX_OPACITY * (0.5 + Math.random() * 0.5),
      state:    "fadein",
      visibleFrames: 800 + Math.random() * 1200,
      life:     0,
      side,
    }
  }

  respawn(p) {
    const fresh = this.make(p.side)
    Object.assign(p, fresh, { y: Math.random() * this.pageH, opacity: 0, state: "fadein", life: 0 })
  }

  // ── Animations-Loop ───────────────────────────────────────────────────────

  tick() {
    this.raf = requestAnimationFrame(() => this.tick())
    const { ctx, VW, VH } = this
    const scrollY = window.scrollY

    ctx.clearRect(0, 0, VW, VH)
    if (!this.hasSpace) return

    for (const p of this.particles) {
      p.y     += p.driftY
      p.phase += SWAY_SPEED
      p.x      = p.baseX + Math.sin(p.phase) * SWAY_AMP
      p.rotation += p.rotSpeed

      p.size += p.sizeSpeed
      if (p.size > MAX_SIZE) { p.size = MAX_SIZE; p.sizeSpeed *= -1 }
      if (p.size < MIN_SIZE) { p.size = MIN_SIZE; p.sizeSpeed *= -1 }

      if (p.y > this.pageH + p.size * 2) p.y = -p.size * 2
      if (p.y < -p.size * 2)             p.y = this.pageH + p.size * 2

      if (p.state === "fadein") {
        p.opacity += FADE_IN_SPEED
        if (p.opacity >= p.maxOpacity) { p.opacity = p.maxOpacity; p.state = "visible" }
      } else if (p.state === "visible") {
        p.life++
        if (p.life >= p.visibleFrames) p.state = "fadeout"
      } else if (p.state === "fadeout") {
        p.opacity -= FADE_OUT_SPEED
        if (p.opacity <= 0) { this.respawn(p); continue }
      }

      const screenY = p.y - scrollY
      if (screenY < -p.size * 2 || screenY > VH + p.size * 2) continue

      if (p.img) this.draw(ctx, p.x, screenY, p)
    }
  }

  draw(ctx, x, y, p) {
    ctx.save()
    ctx.globalAlpha = p.opacity
    ctx.translate(x, y)
    ctx.rotate(p.rotation)
    const s = p.size
    ctx.drawImage(p.img, -s / 2, -s / 2, s, s)
    ctx.restore()
  }

  // ── Events ────────────────────────────────────────────────────────────────

  addListeners() {
    this._onResize = () => this.resize()
    window.addEventListener("resize", this._onResize, { passive: true })

    this._ro = new ResizeObserver(() => {
      const newH = document.documentElement.scrollHeight
      if (Math.abs(newH - this.pageH) > 50) this.resize()
    })
    this._ro.observe(document.body)
  }
}
